import { ControlProps, GroupTypeBase, OptionProps, OptionTypeBase, SingleValueProps, Styles } from 'react-select';
import { CSSObject } from '@emotion/serialize';
import styles from './SelectInput.module.scss';

export function createCustomStyles<T extends OptionTypeBase>(
    hasError: boolean,
    inTableCell?: boolean,
    menuAlignment = 'left',
    storniert = false,
    controlBackgroundTransparent = false
): Partial<Styles<T, false>> {
    const createControlStyle = (_: CSSObject, state: ControlProps<T, false>): CSSObject => {
        const defaultControlStyle: CSSObject = {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            transition: 'all 100ms',
            height: '2.625rem',
        };

        const borderStyleColor = (): string => {
            if (hasError) {
                return styles.errorColor;
            }
            if (state.isDisabled) {
                return styles.borderColorDisabled;
            }
            if (state.isFocused) {
                return styles.focusBorderColor;
            }

            return styles.borderColor;
        };

        const backgroundColor = (): string => {
            if (state.isDisabled) {
                return styles.inputBackgroundColorDisabled;
            }

            if (state.isFocused) {
                return styles.focusBackgroundColor;
            }

            return styles.inputBackgroundColor;
        };

        const bg = controlBackgroundTransparent ? 'transparent' : backgroundColor();
        const borderRadius = controlBackgroundTransparent ? 0 : undefined;
        const singleLineStyle = controlBackgroundTransparent ? { flexWrap: 'nowrap' as const, minHeight: 36, height: 'auto' } : {};

        if (inTableCell) {
            return {
                ...defaultControlStyle,
                border: 'none',
                height: '100%',
                backgroundColor: bg,
                ...(borderRadius !== undefined ? { borderRadius } : {}),
                ...singleLineStyle,
            };
        }

        return {
            ...defaultControlStyle,
            border: `1px solid ${borderStyleColor()}`,
            backgroundColor: bg,
            ...(borderRadius !== undefined ? { borderRadius } : {}),
            ...singleLineStyle,
        };
    };

    const createOptionStyle = (base: CSSObject, state: OptionProps<T, false>): CSSObject => {
        const backgroundColor = (): string => {
            // Focus state should override selected according to tests
            if (state.isFocused) {
                return styles.hoverBackgroundColor;
            }
            if (state.isSelected) {
                return styles.selectedOptionBackgroundColor;
            }
            return 'transparent';
        };

        return {
            ...base,
            backgroundColor: backgroundColor(),
            color: styles.textColor,
            whiteSpace: 'nowrap',
            ':active': {
                ...(base[':active'] as CSSObject),
                backgroundColor: backgroundColor(),
            },
        };
    };

    const createMenuStyle = (base: CSSObject): CSSObject => {
        return {
            ...base,
            borderRadius: 0,
            zIndex: 1000,
            minWidth: 'fit-content',
            [menuAlignment]: 0,
            margin: 0,
        };
    };
    const createMenuPortalStyle = (base: CSSObject): CSSObject => {
        // Ensure the portal is above WebView overlays on Android emulators/devices
        return {
            ...base,
            zIndex: 9999,
            position: 'fixed',
        };
    };

    const createSingleValueStyle = (base: CSSObject, state: SingleValueProps<T, GroupTypeBase<T>>): CSSObject => {
        if (storniert) {
            return { ...base, color: styles.storniertColor, textDecoration: 'line-through' };
        }
        return { ...base, color: state.selectProps.menuIsOpen ? 'lightgrey' : styles.textColor };
    };

    const createPlaceHolderStyle = (base: CSSObject): CSSObject => {
        return {
            ...base,
            fontSize: '1rem',
            top: '53%',
        };
    };

    const createMenuListStyle = (base: CSSObject): CSSObject => {
        return {
            ...base,
            padding: 0,
        };
    };

    return {
        control: createControlStyle,
        option: createOptionStyle,
        menu: createMenuStyle,
        menuPortal: createMenuPortalStyle,
        menuList: createMenuListStyle,
        singleValue: createSingleValueStyle,
        placeholder: createPlaceHolderStyle,
        multiValueRemove: (base): CSSObject => {
            return {
                ...base,
                color: styles.textColor,
                ':hover': {
                    backgroundColor: 'transparent',
                    color: styles.textColor,
                },
            };
        },
        multiValue: (base): CSSObject => {
            return {
                ...base,
                marginTop: 2,
                marginBottom: 2,
                marginRight: 2,
            };
        },
        valueContainer: (provided: CSSObject): CSSObject => {
            return {
                ...provided,
                height: '100%',
                display: 'flex',
                flexWrap: 'nowrap',
                overflowX: 'hidden',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                paddingRight: 4,
                ...(controlBackgroundTransparent ? { backgroundColor: 'transparent', paddingLeft: 0, borderRadius: 0 } : {}),
            };
        },
        indicatorSeparator: (provided: CSSObject, state: { selectProps?: { isDisabled?: boolean } }): CSSObject => {
            const hideIndicator = state.selectProps?.isDisabled === true;
            return { ...provided, marginBottom: 0, marginTop: 0, marginRight: 4, ...(hideIndicator ? { display: 'none' } : {}) };
        },
        indicatorsContainer: (base: CSSObject, state: { selectProps?: { isDisabled?: boolean } }): CSSObject => ({
            ...base,
            paddingRight: 6,
            paddingLeft: 4,
            ...(controlBackgroundTransparent ? { backgroundColor: 'transparent', borderRadius: 0 } : {}),
            ...(state.selectProps?.isDisabled === true ? { display: 'none' } : {}),
        }),
        dropdownIndicator: (base: CSSObject, state: { selectProps?: { isDisabled?: boolean } }): CSSObject => ({
            ...base,
            padding: 6,
            ...(state.selectProps?.isDisabled === true ? { display: 'none' } : {}),
        }),
        input: (base): CSSObject => ({ ...base, margin: 0, padding: 0 }),
    };
}
